import { useMemo } from "react"
import { chain, sortBy, get, size } from "lodash"
import { CLASSIFICATION_OPERATORS } from "helpers/filters/filter-ons/classifications"
import { computeClassifications } from "helpers/classification"
import { flattenClassificationValues, useClassifications, useClassificationsValues } from "resources/classifications"
import { IsoDate } from "helpers/types/dates"
import type { ClassificationFilterOn, ClassificationFilterOnOption } from "helpers/filters/filter-ons"
import { SecuritySearchFilter } from "resources/filters"
import { DataStatus, combineTanStackStatuses } from "helpers/data-statuses"

interface UseClassificationFiltersParams {
  filter: SecuritySearchFilter | null
  selectedDate: IsoDate
}

export function useClassificationFilters({ filter, selectedDate }: UseClassificationFiltersParams): [ClassificationFilterOn[], DataStatus] {
  const [rawClassifications, rawClassificationsStatus] = useClassifications()
  const [classificationValues, classificationValuesStatus] = useClassificationsValues(rawClassifications, selectedDate)

  const classifications = useMemo(() =>
    computeClassifications(rawClassifications, selectedDate),
  [rawClassifications, selectedDate],
  )

  const filters = useMemo(() => {
    if (size(classifications) === 0) {
      return []
    }

    const options: ClassificationFilterOnOption[] = classifications.map(classification => {
      const versionValues = get(classificationValues, [classification.id, "version", "values"], [])
      const tiers = classification.version?.tiers || []
      const options = flattenClassificationValues(tiers, versionValues, 1)
      return {
        label: `${classification.name}`,
        isDisabled: chain(filter)
          .get("classification")
          .some(f => f.id === classification.id)
          .value(),
        type: "classification",
        dataType: "string",
        operators: CLASSIFICATION_OPERATORS,
        options: sortBy(options, "label"),
        value: classification.id,
      }
    })

    const classificationFilter: ClassificationFilterOn = {
      label: "Classification",
      type: "classification",
      options,
    }

    return [classificationFilter]
  }, [classifications, classificationValues, filter])

  const status = combineTanStackStatuses([rawClassificationsStatus, classificationValuesStatus])

  return [filters, status]
}
